import { Actions } from '../enums/actions';
import { Subjects } from '../enums/subjects';

export type CapabilityDomain =
  | 'iam'
  | 'projects'
  | 'sites'
  | 'vendor-management'
  | 'boq'
  | 'orders'
  | 'payments-prs-aps'
  | 'compliance'
  | 'returns'
  | 'dashboards'
  | 'cashflow'
  | 'bank-draws'
  | 'navigation';

export interface CapabilityDefinition {
  /**
  Stable key, `<domain>.<label-slug>`.
  */
  readonly id: string;
  readonly domain: CapabilityDomain;
  /**
  i18n key; the English label lives with the consuming client.
  */
  readonly labelKey: string;
  /**
  Exactly what the app passes to `can()`, `MANAGE` included where the app gates on it.
  */
  readonly action: Actions;
  readonly subject: Subjects;
  /**
  The request kind the app passes with the check, for a grant that names one (a provisional-budget
  decide grant, DL-67). The resolver and the truth tables merge it into every check they make, so
  each (action, kind) pair is its own entry and its own row.
  */
  readonly kind?: string;
}

/**
 * BudgetControl capabilities the frontend checks, labelled for display.
 *
 * Context: one entry per permission-spreadsheet row in `tests/truth-table/` (so several labels
 * can share one check, like the AP tabs reading `AccountsPayable`), plus an entry for every other
 * grant the factory makes, `MANAGE` included, which `capability-coverage.test.ts` enforces.
 * VendorControl subjects are deliberately absent: VendorControl has its own permissions surface.
 */
export const CAPABILITY_CATALOGUE: readonly CapabilityDefinition[] = [
  {
    id: 'iam.create-organisation',
    domain: 'iam',
    labelKey: 'capabilities.iam.createOrganisation',
    action: Actions.CREATE,
    subject: Subjects.ORGANISATION,
  },
  {
    id: 'iam.view-organisation',
    domain: 'iam',
    labelKey: 'capabilities.iam.viewOrganisation',
    action: Actions.READ,
    subject: Subjects.ORGANISATION,
  },
  {
    id: 'iam.view-organisation-users',
    domain: 'iam',
    labelKey: 'capabilities.iam.viewOrganisationUsers',
    action: Actions.READ,
    subject: Subjects.ORG_USER,
  },
  {
    id: 'iam.edit-organisation',
    domain: 'iam',
    labelKey: 'capabilities.iam.editOrganisation',
    action: Actions.UPDATE,
    subject: Subjects.ORGANISATION,
  },
  {
    id: 'iam.manage-organisation-users',
    domain: 'iam',
    labelKey: 'capabilities.iam.manageOrganisationUsers',
    action: Actions.MANAGE,
    subject: Subjects.ORG_USER,
  },
  {
    id: 'iam.list-organisations',
    domain: 'iam',
    labelKey: 'capabilities.iam.listOrganisations',
    action: Actions.LIST,
    subject: Subjects.ORGANISATION,
  },
  {
    id: 'projects.create-project',
    domain: 'projects',
    labelKey: 'capabilities.projects.createProject',
    action: Actions.CREATE,
    subject: Subjects.PROJECT,
  },
  {
    id: 'projects.list-projects',
    domain: 'projects',
    labelKey: 'capabilities.projects.listProjects',
    action: Actions.LIST,
    subject: Subjects.PROJECT,
  },
  {
    id: 'projects.view-project',
    domain: 'projects',
    labelKey: 'capabilities.projects.viewProject',
    action: Actions.READ,
    subject: Subjects.PROJECT,
  },
  {
    id: 'projects.delete-archive-project',
    domain: 'projects',
    labelKey: 'capabilities.projects.deleteArchiveProject',
    action: Actions.DELETE,
    subject: Subjects.PROJECT,
  },
  {
    id: 'projects.manage-project-users',
    domain: 'projects',
    labelKey: 'capabilities.projects.manageProjectUsers',
    action: Actions.MANAGE,
    subject: Subjects.PROJECT_USER,
  },
  {
    id: 'projects.edit-project',
    domain: 'projects',
    labelKey: 'capabilities.projects.editProject',
    action: Actions.UPDATE,
    subject: Subjects.PROJECT,
  },
  {
    id: 'projects.view-project-budget-summaries',
    domain: 'projects',
    labelKey: 'capabilities.projects.viewProjectBudgetSummaries',
    action: Actions.READ,
    subject: Subjects.PROJECT_BUDGET_SUMMARY_CARD,
  },
  {
    id: 'projects.manage-projects',
    domain: 'projects',
    labelKey: 'capabilities.projects.manageProjects',
    action: Actions.MANAGE,
    subject: Subjects.PROJECT,
  },
  {
    id: 'sites.create-site',
    domain: 'sites',
    labelKey: 'capabilities.sites.createSite',
    action: Actions.CREATE,
    subject: Subjects.SITE,
  },
  {
    id: 'sites.list-sites',
    domain: 'sites',
    labelKey: 'capabilities.sites.listSites',
    action: Actions.LIST,
    subject: Subjects.SITE,
  },
  {
    id: 'sites.view-site',
    domain: 'sites',
    labelKey: 'capabilities.sites.viewSite',
    action: Actions.READ,
    subject: Subjects.SITE,
  },
  {
    id: 'sites.edit-site',
    domain: 'sites',
    labelKey: 'capabilities.sites.editSite',
    action: Actions.UPDATE,
    subject: Subjects.SITE,
  },
  {
    id: 'sites.view-project-budget-summary',
    domain: 'sites',
    labelKey: 'capabilities.sites.viewProjectBudgetSummary',
    action: Actions.READ,
    subject: Subjects.SITE_PROJECT_BUDGET_SUMMARY_TILES,
  },
  {
    id: 'sites.manage-site-users',
    domain: 'sites',
    labelKey: 'capabilities.sites.manageSiteUsers',
    action: Actions.MANAGE,
    subject: Subjects.SITE_USER,
  },
  {
    id: 'sites.manage-sites',
    domain: 'sites',
    labelKey: 'capabilities.sites.manageSites',
    action: Actions.MANAGE,
    subject: Subjects.SITE,
  },
  {
    id: 'vendor-management.create-custom-vendor',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.createCustomVendor',
    action: Actions.CREATE,
    subject: Subjects.ORG_VENDOR,
  },
  {
    id: 'vendor-management.create-boq-vendor',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.createBoqVendor',
    action: Actions.CREATE,
    subject: Subjects.BOQ_VENDOR,
  },
  {
    id: 'vendor-management.list-org-vendors',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.listOrgVendors',
    action: Actions.LIST,
    subject: Subjects.ORG_VENDOR,
  },
  {
    id: 'vendor-management.list-boq-vendors',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.listBoqVendors',
    action: Actions.LIST,
    subject: Subjects.BOQ_VENDOR,
  },
  {
    id: 'vendor-management.view-org-vendor',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.viewOrgVendor',
    action: Actions.READ,
    subject: Subjects.ORG_VENDOR,
  },
  {
    id: 'vendor-management.view-boq-vendor',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.viewBoqVendor',
    action: Actions.READ,
    subject: Subjects.BOQ_VENDOR,
  },
  {
    id: 'vendor-management.edit-org-vendor',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.editOrgVendor',
    action: Actions.UPDATE,
    subject: Subjects.ORG_VENDOR,
  },
  {
    id: 'vendor-management.edit-boq-vendor',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.editBoqVendor',
    action: Actions.UPDATE,
    subject: Subjects.BOQ_VENDOR,
  },
  {
    id: 'vendor-management.delete-org-vendor',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.deleteOrgVendor',
    action: Actions.DELETE,
    subject: Subjects.ORG_VENDOR,
  },
  {
    id: 'vendor-management.delete-boq-vendor',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.deleteBoqVendor',
    action: Actions.DELETE,
    subject: Subjects.BOQ_VENDOR,
  },
  {
    id: 'vendor-management.sync-vendors',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.syncVendors',
    action: Actions.SYNC,
    subject: Subjects.BOQ_VENDOR,
  },
  {
    id: 'vendor-management.create-org-item',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.createOrgItem',
    action: Actions.CREATE,
    subject: Subjects.ORG_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.create-boq-item',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.createBoqItem',
    action: Actions.CREATE,
    subject: Subjects.BOQ_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.list-org-items',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.listOrgItems',
    action: Actions.LIST,
    subject: Subjects.ORG_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.list-boq-items',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.listBoqItems',
    action: Actions.LIST,
    subject: Subjects.BOQ_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.view-org-item',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.viewOrgItem',
    action: Actions.READ,
    subject: Subjects.ORG_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.view-boq-item',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.viewBoqItem',
    action: Actions.READ,
    subject: Subjects.BOQ_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.edit-org-item',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.editOrgItem',
    action: Actions.UPDATE,
    subject: Subjects.ORG_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.edit-boq-item',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.editBoqItem',
    action: Actions.UPDATE,
    subject: Subjects.BOQ_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.delete-org-item',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.deleteOrgItem',
    action: Actions.DELETE,
    subject: Subjects.ORG_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.delete-boq-item',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.deleteBoqItem',
    action: Actions.DELETE,
    subject: Subjects.BOQ_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.bulk-upload-org-items',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.bulkUploadOrgItems',
    action: Actions.BULK_UPLOAD,
    subject: Subjects.ORG_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.bulk-upload-boq-items',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.bulkUploadBoqItems',
    action: Actions.BULK_UPLOAD,
    subject: Subjects.BOQ_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.manage-org-vendors',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.manageOrgVendors',
    action: Actions.MANAGE,
    subject: Subjects.ORG_VENDOR,
  },
  {
    id: 'vendor-management.manage-boq-vendors',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.manageBoqVendors',
    action: Actions.MANAGE,
    subject: Subjects.BOQ_VENDOR,
  },
  {
    id: 'vendor-management.manage-org-items',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.manageOrgItems',
    action: Actions.MANAGE,
    subject: Subjects.ORG_VENDOR_ITEM,
  },
  {
    id: 'vendor-management.manage-boq-items',
    domain: 'vendor-management',
    labelKey: 'capabilities.vendorManagement.manageBoqItems',
    action: Actions.MANAGE,
    subject: Subjects.BOQ_VENDOR_ITEM,
  },
  {
    id: 'boq.create-boq',
    domain: 'boq',
    labelKey: 'capabilities.boq.createBoq',
    action: Actions.CREATE,
    subject: Subjects.BOQ,
  },
  {
    id: 'boq.delete-boq',
    domain: 'boq',
    labelKey: 'capabilities.boq.deleteBoq',
    action: Actions.DELETE,
    subject: Subjects.BOQ,
  },
  {
    id: 'boq.view-cost-boq',
    domain: 'boq',
    labelKey: 'capabilities.boq.viewCostBoq',
    action: Actions.READ,
    subject: Subjects.BOQ,
  },
  {
    id: 'boq.view-selling-boq',
    domain: 'boq',
    labelKey: 'capabilities.boq.viewSellingBoq',
    action: Actions.READ,
    subject: Subjects.SELLING_BOQ,
  },
  {
    id: 'boq.export-boq',
    domain: 'boq',
    labelKey: 'capabilities.boq.exportBoq',
    action: Actions.EXPORT,
    subject: Subjects.BOQ,
  },
  {
    id: 'boq.submit-boq-for-approval',
    domain: 'boq',
    labelKey: 'capabilities.boq.submitBoqForApproval',
    action: Actions.SUBMIT,
    subject: Subjects.BOQ,
  },
  {
    id: 'boq.approve-boq',
    domain: 'boq',
    labelKey: 'capabilities.boq.approveBoq',
    action: Actions.APPROVE,
    subject: Subjects.BOQ,
  },
  {
    id: 'boq.reject-boq',
    domain: 'boq',
    labelKey: 'capabilities.boq.rejectBoq',
    action: Actions.DECLINE,
    subject: Subjects.BOQ,
  },
  {
    id: 'boq.view-boq-templates',
    domain: 'boq',
    labelKey: 'capabilities.boq.viewBoqTemplates',
    action: Actions.READ,
    subject: Subjects.BOQ_TEMPLATE,
  },
  {
    id: 'boq.create-boq-template',
    domain: 'boq',
    labelKey: 'capabilities.boq.createBoqTemplate',
    action: Actions.CREATE,
    subject: Subjects.BOQ_TEMPLATE,
  },
  {
    id: 'boq.delete-boq-template',
    domain: 'boq',
    labelKey: 'capabilities.boq.deleteBoqTemplate',
    action: Actions.DELETE,
    subject: Subjects.BOQ_TEMPLATE,
  },
  {
    id: 'boq.make-boq-template-public',
    domain: 'boq',
    labelKey: 'capabilities.boq.makeBoqTemplatePublic',
    action: Actions.PUBLISH,
    subject: Subjects.BOQ_TEMPLATE,
  },
  {
    id: 'boq.add-boq-section-item-group',
    domain: 'boq',
    labelKey: 'capabilities.boq.addBoqSectionItemGroup',
    action: Actions.CREATE,
    subject: Subjects.BOQ_ITEM,
  },
  {
    id: 'boq.edit-boq-section-item-group',
    domain: 'boq',
    labelKey: 'capabilities.boq.editBoqSectionItemGroup',
    action: Actions.UPDATE,
    subject: Subjects.BOQ_ITEM,
  },
  {
    id: 'boq.delete-boq-section-item-group',
    domain: 'boq',
    labelKey: 'capabilities.boq.deleteBoqSectionItemGroup',
    action: Actions.DELETE,
    subject: Subjects.BOQ_ITEM,
  },
  {
    id: 'boq.add-boq-variation',
    domain: 'boq',
    labelKey: 'capabilities.boq.addBoqVariation',
    action: Actions.CREATE,
    subject: Subjects.BOQ_VARIATION,
  },
  {
    id: 'boq.approve-boq-variation',
    domain: 'boq',
    labelKey: 'capabilities.boq.approveBoqVariation',
    action: Actions.APPROVE,
    subject: Subjects.BOQ_VARIATION,
  },
  {
    id: 'boq.reject-boq-variation',
    domain: 'boq',
    labelKey: 'capabilities.boq.rejectBoqVariation',
    action: Actions.DECLINE,
    subject: Subjects.BOQ_VARIATION,
  },
  {
    id: 'boq.list-client-variations',
    domain: 'boq',
    labelKey: 'capabilities.boq.listClientVariations',
    action: Actions.LIST,
    subject: Subjects.CLIENT_VARIATION,
  },
  {
    id: 'boq.view-client-variations',
    domain: 'boq',
    labelKey: 'capabilities.boq.viewClientVariations',
    action: Actions.READ,
    subject: Subjects.CLIENT_VARIATION,
  },
  {
    id: 'boq.create-client-variation',
    domain: 'boq',
    labelKey: 'capabilities.boq.createClientVariation',
    action: Actions.CREATE,
    subject: Subjects.CLIENT_VARIATION,
  },
  {
    id: 'boq.edit-client-variation',
    domain: 'boq',
    labelKey: 'capabilities.boq.editClientVariation',
    action: Actions.UPDATE,
    subject: Subjects.CLIENT_VARIATION,
  },
  {
    id: 'boq.cancel-client-variation',
    domain: 'boq',
    labelKey: 'capabilities.boq.cancelClientVariation',
    action: Actions.CANCEL,
    subject: Subjects.CLIENT_VARIATION,
  },
  {
    id: 'boq.approve-client-variation',
    domain: 'boq',
    labelKey: 'capabilities.boq.approveClientVariation',
    action: Actions.APPROVE,
    subject: Subjects.CLIENT_VARIATION,
  },
  {
    id: 'boq.deny-client-variation',
    domain: 'boq',
    labelKey: 'capabilities.boq.denyClientVariation',
    action: Actions.DECLINE,
    subject: Subjects.CLIENT_VARIATION,
  },
  {
    id: 'boq.log-client-variation-payment',
    domain: 'boq',
    labelKey: 'capabilities.boq.logClientVariationPayment',
    action: Actions.CREATE,
    subject: Subjects.CLIENT_VARIATION_PAYMENT,
  },
  {
    id: 'boq.view-provisional-budgets',
    domain: 'boq',
    labelKey: 'capabilities.boq.viewProvisionalBudgets',
    action: Actions.READ,
    subject: Subjects.PROVISIONAL_BUDGET,
  },
  {
    id: 'boq.create-provisional-budget',
    domain: 'boq',
    labelKey: 'capabilities.boq.createProvisionalBudget',
    action: Actions.CREATE,
    subject: Subjects.PROVISIONAL_BUDGET,
  },
  // Context: the create's decide entries keep their M1 ids and label keys; the kind was added beside
  // them when allocations became the second decision kind (DL-67).
  {
    id: 'boq.approve-provisional-budget-request',
    domain: 'boq',
    labelKey: 'capabilities.boq.approveProvisionalBudgetRequest',
    action: Actions.APPROVE,
    subject: Subjects.PROVISIONAL_BUDGET,
    kind: 'Create',
  },
  {
    id: 'boq.decline-provisional-budget-request',
    domain: 'boq',
    labelKey: 'capabilities.boq.declineProvisionalBudgetRequest',
    action: Actions.DECLINE,
    subject: Subjects.PROVISIONAL_BUDGET,
    kind: 'Create',
  },
  {
    id: 'boq.approve-provisional-budget-allocation',
    domain: 'boq',
    labelKey: 'capabilities.boq.approveProvisionalBudgetAllocation',
    action: Actions.APPROVE,
    subject: Subjects.PROVISIONAL_BUDGET,
    kind: 'Allocation',
  },
  {
    id: 'boq.decline-provisional-budget-allocation',
    domain: 'boq',
    labelKey: 'capabilities.boq.declineProvisionalBudgetAllocation',
    action: Actions.DECLINE,
    subject: Subjects.PROVISIONAL_BUDGET,
    kind: 'Allocation',
  },
  {
    id: 'boq.manage-boq',
    domain: 'boq',
    labelKey: 'capabilities.boq.manageBoq',
    action: Actions.MANAGE,
    subject: Subjects.BOQ,
  },
  {
    id: 'boq.edit-boq',
    domain: 'boq',
    labelKey: 'capabilities.boq.editBoq',
    action: Actions.UPDATE,
    subject: Subjects.BOQ,
  },
  {
    id: 'boq.manage-boq-sections-items-groups',
    domain: 'boq',
    labelKey: 'capabilities.boq.manageBoqSectionsItemsGroups',
    action: Actions.MANAGE,
    subject: Subjects.BOQ_ITEM,
  },
  {
    id: 'boq.manage-boq-variations',
    domain: 'boq',
    labelKey: 'capabilities.boq.manageBoqVariations',
    action: Actions.MANAGE,
    subject: Subjects.BOQ_VARIATION,
  },
  {
    id: 'orders.add-item-to-cart',
    domain: 'orders',
    labelKey: 'capabilities.orders.addItemToCart',
    action: Actions.CREATE,
    subject: Subjects.CART,
  },
  {
    id: 'orders.view-cart',
    domain: 'orders',
    labelKey: 'capabilities.orders.viewCart',
    action: Actions.READ,
    subject: Subjects.CART,
  },
  {
    id: 'orders.edit-cart',
    domain: 'orders',
    labelKey: 'capabilities.orders.editCart',
    action: Actions.UPDATE,
    subject: Subjects.CART,
  },
  {
    id: 'orders.edit-cart-items',
    domain: 'orders',
    labelKey: 'capabilities.orders.editCartItems',
    action: Actions.UPDATE,
    subject: Subjects.CART,
  },
  {
    id: 'orders.remove-cart-items',
    domain: 'orders',
    labelKey: 'capabilities.orders.removeCartItems',
    action: Actions.DELETE,
    subject: Subjects.CART,
  },
  {
    id: 'orders.clear-cart',
    domain: 'orders',
    labelKey: 'capabilities.orders.clearCart',
    action: Actions.DELETE,
    subject: Subjects.CART,
  },
  {
    id: 'orders.create-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.createOrder',
    action: Actions.CREATE,
    subject: Subjects.ORDER,
  },
  {
    id: 'orders.approve-order-variations',
    domain: 'orders',
    labelKey: 'capabilities.orders.approveOrderVariations',
    action: Actions.APPROVE,
    subject: Subjects.ORDER_VARIATION,
  },
  {
    id: 'orders.reject-order-variations',
    domain: 'orders',
    labelKey: 'capabilities.orders.rejectOrderVariations',
    action: Actions.DECLINE,
    subject: Subjects.ORDER_VARIATION,
  },
  {
    id: 'orders.list-orders',
    domain: 'orders',
    labelKey: 'capabilities.orders.listOrders',
    action: Actions.LIST,
    subject: Subjects.ORDER,
  },
  {
    id: 'orders.view-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.viewOrder',
    action: Actions.READ,
    subject: Subjects.ORDER,
  },
  {
    id: 'orders.export-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.exportOrder',
    action: Actions.EXPORT,
    subject: Subjects.ORDER,
  },
  {
    id: 'orders.share-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.shareOrder',
    action: Actions.SHARE,
    subject: Subjects.ORDER,
  },
  {
    id: 'orders.upload-order-documentation',
    domain: 'orders',
    labelKey: 'capabilities.orders.uploadOrderDocumentation',
    action: Actions.UPLOAD,
    subject: Subjects.ORDER_DOCUMENTATION,
  },
  {
    id: 'orders.remove-order-documentation',
    domain: 'orders',
    labelKey: 'capabilities.orders.removeOrderDocumentation',
    action: Actions.DELETE,
    subject: Subjects.ORDER_DOCUMENTATION,
  },
  {
    id: 'orders.cancel-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.cancelOrder',
    action: Actions.CANCEL,
    subject: Subjects.ORDER,
  },
  {
    id: 'orders.approve-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.approveOrder',
    action: Actions.APPROVE,
    subject: Subjects.ORDER,
  },
  {
    id: 'orders.reject-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.rejectOrder',
    action: Actions.DECLINE,
    subject: Subjects.ORDER,
  },
  {
    id: 'orders.log-order-delivery',
    domain: 'orders',
    labelKey: 'capabilities.orders.logOrderDelivery',
    action: Actions.CREATE,
    subject: Subjects.ORDER_DELIVERY,
  },
  {
    id: 'orders.list-bulk-orders',
    domain: 'orders',
    labelKey: 'capabilities.orders.listBulkOrders',
    action: Actions.LIST,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.view-bulk-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.viewBulkOrder',
    action: Actions.READ,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.create-bulk-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.createBulkOrder',
    action: Actions.CREATE,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.approve-bulk-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.approveBulkOrder',
    action: Actions.APPROVE,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.decline-bulk-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.declineBulkOrder',
    action: Actions.DECLINE,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.cancel-bulk-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.cancelBulkOrder',
    action: Actions.CANCEL,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.log-bulk-order-delivery',
    domain: 'orders',
    labelKey: 'capabilities.orders.logBulkOrderDelivery',
    action: Actions.LOG_DELIVERY,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.complete-bulk-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.completeBulkOrder',
    action: Actions.COMPLETE,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.share-bulk-order',
    domain: 'orders',
    labelKey: 'capabilities.orders.shareBulkOrder',
    action: Actions.SHARE,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.mark-bulk-order-vendor-notified',
    domain: 'orders',
    labelKey: 'capabilities.orders.markBulkOrderVendorNotified',
    action: Actions.UPDATE,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.upload-bulk-order-document',
    domain: 'orders',
    labelKey: 'capabilities.orders.uploadBulkOrderDocument',
    action: Actions.UPLOAD,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.delete-bulk-order-document',
    domain: 'orders',
    labelKey: 'capabilities.orders.deleteBulkOrderDocument',
    action: Actions.DELETE,
    subject: Subjects.BULK_ORDER,
  },
  {
    id: 'orders.manage-cart',
    domain: 'orders',
    labelKey: 'capabilities.orders.manageCart',
    action: Actions.MANAGE,
    subject: Subjects.CART,
  },
  {
    id: 'orders.manage-orders',
    domain: 'orders',
    labelKey: 'capabilities.orders.manageOrders',
    action: Actions.MANAGE,
    subject: Subjects.ORDER,
  },
  {
    id: 'orders.manage-order-variations',
    domain: 'orders',
    labelKey: 'capabilities.orders.manageOrderVariations',
    action: Actions.MANAGE,
    subject: Subjects.ORDER_VARIATION,
  },
  {
    id: 'payments-prs-aps.list-payment-requests',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.listPaymentRequests',
    action: Actions.LIST,
    subject: Subjects.PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.view-payment-request',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewPaymentRequest',
    action: Actions.READ,
    subject: Subjects.PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.decline-bulk-payment-request',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.declineBulkPaymentRequest',
    action: Actions.DECLINE,
    subject: Subjects.BULK_PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.verify-bulk-payment-request',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.verifyBulkPaymentRequest',
    action: Actions.VERIFY,
    subject: Subjects.BULK_PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.approve-bulk-payment-request',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.approveBulkPaymentRequest',
    action: Actions.APPROVE,
    subject: Subjects.BULK_PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.push-back-bulk-payment-request',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.pushBackBulkPaymentRequest',
    action: Actions.PUSH_BACK,
    subject: Subjects.BULK_PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.approve-payment-request',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.approvePaymentRequest',
    action: Actions.APPROVE,
    subject: Subjects.PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.verify-payment-request',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.verifyPaymentRequest',
    action: Actions.VERIFY,
    subject: Subjects.PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.update-payment-request',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.updatePaymentRequest',
    action: Actions.UPDATE,
    subject: Subjects.PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.push-back-payment-request',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.pushBackPaymentRequest',
    action: Actions.PUSH_BACK,
    subject: Subjects.PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.view-payment-requests-totals',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewPaymentRequestsTotals',
    action: Actions.READ,
    subject: Subjects.PAYMENT_REQUEST,
  },
  {
    id: 'payments-prs-aps.list-accounts-payable',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.listAccountsPayable',
    action: Actions.LIST,
    subject: Subjects.ACCOUNTS_PAYABLE,
  },
  {
    id: 'payments-prs-aps.view-accounts-payable-totals',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewAccountsPayableTotals',
    action: Actions.READ,
    subject: Subjects.ACCOUNTS_PAYABLE,
  },
  {
    id: 'payments-prs-aps.view-ap-log',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewApLog',
    action: Actions.READ,
    subject: Subjects.ACCOUNTS_PAYABLE,
  },
  {
    id: 'payments-prs-aps.view-ap-orders',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewApOrders',
    action: Actions.READ,
    subject: Subjects.ACCOUNTS_PAYABLE,
  },
  {
    id: 'payments-prs-aps.view-ap-claims',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewApClaims',
    action: Actions.READ,
    subject: Subjects.ACCOUNTS_PAYABLE,
  },
  {
    id: 'payments-prs-aps.view-ap-prs',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewApPrs',
    action: Actions.READ,
    subject: Subjects.ACCOUNTS_PAYABLE,
  },
  {
    id: 'payments-prs-aps.view-ap-statements',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewApStatements',
    action: Actions.READ,
    subject: Subjects.ACCOUNTS_PAYABLE,
  },
  {
    id: 'payments-prs-aps.generate-ap-statement',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.generateApStatement',
    action: Actions.CREATE,
    subject: Subjects.AP_STATEMENT,
  },
  {
    id: 'payments-prs-aps.share-ap-statement',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.shareApStatement',
    action: Actions.SHARE,
    subject: Subjects.AP_STATEMENT,
  },
  {
    id: 'payments-prs-aps.delete-ap-statement',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.deleteApStatement',
    action: Actions.DELETE,
    subject: Subjects.AP_STATEMENT,
  },
  {
    id: 'payments-prs-aps.view-ap-payment-history',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewApPaymentHistory',
    action: Actions.READ,
    subject: Subjects.ACCOUNTS_PAYABLE,
  },
  {
    id: 'payments-prs-aps.list-payments',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.listPayments',
    action: Actions.LIST,
    subject: Subjects.PAYMENT,
  },
  {
    id: 'payments-prs-aps.create-log-payment',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.createLogPayment',
    action: Actions.CREATE,
    subject: Subjects.PAYMENT,
  },
  {
    id: 'payments-prs-aps.view-proof-of-payment',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewProofOfPayment',
    action: Actions.READ,
    subject: Subjects.PAYMENT,
  },
  {
    id: 'payments-prs-aps.view-payments-surface',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.viewPaymentsSurface',
    action: Actions.LIST,
    subject: Subjects.PAYMENTS_SURFACE,
  },
  {
    id: 'payments-prs-aps.upload-pr-documentation',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.uploadPrDocumentation',
    action: Actions.UPLOAD,
    subject: Subjects.PR_DOCUMENTATION,
  },
  {
    id: 'payments-prs-aps.remove-pr-documentation',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.removePrDocumentation',
    action: Actions.DELETE,
    subject: Subjects.PR_DOCUMENTATION,
  },
  {
    id: 'payments-prs-aps.create-adhoc-payment',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.createAdhocPayment',
    action: Actions.CREATE,
    subject: Subjects.ADHOC_PAYMENT,
  },
  {
    id: 'payments-prs-aps.manage-accounts-payable',
    domain: 'payments-prs-aps',
    labelKey: 'capabilities.paymentsPrsAps.manageAccountsPayable',
    action: Actions.MANAGE,
    subject: Subjects.ACCOUNTS_PAYABLE,
  },
  {
    id: 'compliance.view-site-compliance',
    domain: 'compliance',
    labelKey: 'capabilities.compliance.viewSiteCompliance',
    action: Actions.READ,
    subject: Subjects.COMPLIANCE,
  },
  {
    id: 'compliance.view-templates',
    domain: 'compliance',
    labelKey: 'capabilities.compliance.viewTemplates',
    action: Actions.READ,
    subject: Subjects.COMPLIANCE_TEMPLATE,
  },
  {
    id: 'compliance.upload-documents',
    domain: 'compliance',
    labelKey: 'capabilities.compliance.uploadDocuments',
    action: Actions.UPLOAD,
    subject: Subjects.COMPLIANCE_DOCUMENT,
  },
  {
    id: 'compliance.remove-documents',
    domain: 'compliance',
    labelKey: 'capabilities.compliance.removeDocuments',
    action: Actions.DELETE,
    subject: Subjects.COMPLIANCE_DOCUMENT,
  },
  {
    id: 'compliance.upload-template',
    domain: 'compliance',
    labelKey: 'capabilities.compliance.uploadTemplate',
    action: Actions.UPLOAD,
    subject: Subjects.COMPLIANCE_TEMPLATE,
  },
  {
    id: 'compliance.delete-template',
    domain: 'compliance',
    labelKey: 'capabilities.compliance.deleteTemplate',
    action: Actions.DELETE,
    subject: Subjects.COMPLIANCE_TEMPLATE,
  },
  {
    id: 'compliance.change-site-dates',
    domain: 'compliance',
    labelKey: 'capabilities.compliance.changeSiteDates',
    action: Actions.UPDATE,
    subject: Subjects.SITE,
  },
  {
    id: 'compliance.download-documents',
    domain: 'compliance',
    labelKey: 'capabilities.compliance.downloadDocuments',
    action: Actions.DOWNLOAD,
    subject: Subjects.COMPLIANCE_DOCUMENT,
  },
  {
    id: 'returns.create-a-return',
    domain: 'returns',
    labelKey: 'capabilities.returns.createAReturn',
    action: Actions.CREATE,
    subject: Subjects.RETURN,
  },
  {
    id: 'returns.approve-a-return',
    domain: 'returns',
    labelKey: 'capabilities.returns.approveAReturn',
    action: Actions.APPROVE,
    subject: Subjects.RETURN,
  },
  {
    id: 'returns.reject-a-return',
    domain: 'returns',
    labelKey: 'capabilities.returns.rejectAReturn',
    action: Actions.DECLINE,
    subject: Subjects.RETURN,
  },
  {
    id: 'returns.list-returns',
    domain: 'returns',
    labelKey: 'capabilities.returns.listReturns',
    action: Actions.LIST,
    subject: Subjects.RETURN,
  },
  {
    id: 'returns.view-a-return',
    domain: 'returns',
    labelKey: 'capabilities.returns.viewAReturn',
    action: Actions.READ,
    subject: Subjects.RETURN,
  },
  {
    id: 'returns.cancel-a-return',
    domain: 'returns',
    labelKey: 'capabilities.returns.cancelAReturn',
    action: Actions.CANCEL,
    subject: Subjects.RETURN,
  },
  {
    id: 'returns.list-credits-and-refunds',
    domain: 'returns',
    labelKey: 'capabilities.returns.listCreditsAndRefunds',
    action: Actions.LIST,
    subject: Subjects.CREDIT,
  },
  {
    id: 'returns.view-a-credit',
    domain: 'returns',
    labelKey: 'capabilities.returns.viewACredit',
    action: Actions.READ,
    subject: Subjects.CREDIT,
  },
  {
    id: 'returns.view-a-refund',
    domain: 'returns',
    labelKey: 'capabilities.returns.viewARefund',
    action: Actions.READ,
    subject: Subjects.REFUND,
  },
  {
    id: 'returns.cancel-a-credit',
    domain: 'returns',
    labelKey: 'capabilities.returns.cancelACredit',
    action: Actions.CANCEL,
    subject: Subjects.CREDIT,
  },
  {
    id: 'returns.cancel-a-refund',
    domain: 'returns',
    labelKey: 'capabilities.returns.cancelARefund',
    action: Actions.CANCEL,
    subject: Subjects.REFUND,
  },
  {
    id: 'returns.complete-a-credit',
    domain: 'returns',
    labelKey: 'capabilities.returns.completeACredit',
    action: Actions.COMPLETE,
    subject: Subjects.CREDIT,
  },
  {
    id: 'returns.complete-a-refund',
    domain: 'returns',
    labelKey: 'capabilities.returns.completeARefund',
    action: Actions.COMPLETE,
    subject: Subjects.REFUND,
  },
  {
    id: 'returns.edit-a-return',
    domain: 'returns',
    labelKey: 'capabilities.returns.editAReturn',
    action: Actions.UPDATE,
    subject: Subjects.RETURN,
  },
  {
    id: 'returns.list-refunds',
    domain: 'returns',
    labelKey: 'capabilities.returns.listRefunds',
    action: Actions.LIST,
    subject: Subjects.REFUND,
  },
  {
    id: 'dashboards.view-org-dashboard',
    domain: 'dashboards',
    labelKey: 'capabilities.dashboards.viewOrgDashboard',
    action: Actions.READ,
    subject: Subjects.ORG_DASHBOARD,
  },
  {
    id: 'dashboards.change-financial-year',
    domain: 'dashboards',
    labelKey: 'capabilities.dashboards.changeFinancialYear',
    action: Actions.UPDATE,
    subject: Subjects.ORG_DASHBOARD,
  },
  {
    id: 'dashboards.view-client-dashboard',
    domain: 'dashboards',
    labelKey: 'capabilities.dashboards.viewClientDashboard',
    action: Actions.READ,
    subject: Subjects.CLIENT_DASHBOARD,
  },
  {
    id: 'cashflow.view-project-cashflow',
    domain: 'cashflow',
    labelKey: 'capabilities.cashflow.viewProjectCashflow',
    action: Actions.READ,
    subject: Subjects.PROJECT_CASHFLOW,
  },
  {
    id: 'cashflow.update-overheads',
    domain: 'cashflow',
    labelKey: 'capabilities.cashflow.updateOverheads',
    action: Actions.UPDATE,
    subject: Subjects.SITE_CASHFLOW,
  },
  {
    id: 'cashflow.export-cashflow',
    domain: 'cashflow',
    labelKey: 'capabilities.cashflow.exportCashflow',
    action: Actions.EXPORT,
    subject: Subjects.SITE_CASHFLOW,
  },
  {
    id: 'cashflow.view-site-cashflow',
    domain: 'cashflow',
    labelKey: 'capabilities.cashflow.viewSiteCashflow',
    action: Actions.READ,
    subject: Subjects.SITE_CASHFLOW,
  },
  {
    id: 'cashflow.export-project-cashflow',
    domain: 'cashflow',
    labelKey: 'capabilities.cashflow.exportProjectCashflow',
    action: Actions.EXPORT,
    subject: Subjects.PROJECT_CASHFLOW,
  },
  {
    id: 'cashflow.update-project-overheads',
    domain: 'cashflow',
    labelKey: 'capabilities.cashflow.updateProjectOverheads',
    action: Actions.UPDATE,
    subject: Subjects.PROJECT_CASHFLOW,
  },
  {
    id: 'bank-draws.view-bank-draws',
    domain: 'bank-draws',
    labelKey: 'capabilities.bankDraws.viewBankDraws',
    action: Actions.READ,
    subject: Subjects.BANK_DRAW,
  },
  {
    id: 'bank-draws.update-draw-amounts',
    domain: 'bank-draws',
    labelKey: 'capabilities.bankDraws.updateDrawAmounts',
    action: Actions.UPDATE,
    subject: Subjects.BANK_DRAW,
  },
  {
    id: 'bank-draws.update-draw-schedule',
    domain: 'bank-draws',
    labelKey: 'capabilities.bankDraws.updateDrawSchedule',
    action: Actions.UPDATE,
    subject: Subjects.DRAW_SCHEDULE,
  },
  {
    id: 'bank-draws.view-claim-sheet',
    domain: 'bank-draws',
    labelKey: 'capabilities.bankDraws.viewClaimSheet',
    action: Actions.READ,
    subject: Subjects.CLAIM_SHEET,
  },
  {
    id: 'bank-draws.generate-claim-sheet',
    domain: 'bank-draws',
    labelKey: 'capabilities.bankDraws.generateClaimSheet',
    action: Actions.CREATE,
    subject: Subjects.CLAIM_SHEET,
  },
  {
    id: 'bank-draws.list-bank-draws',
    domain: 'bank-draws',
    labelKey: 'capabilities.bankDraws.listBankDraws',
    action: Actions.LIST,
    subject: Subjects.BANK_DRAW,
  },
  {
    id: 'navigation.nav-dashboard',
    domain: 'navigation',
    labelKey: 'capabilities.navigation.navDashboard',
    action: Actions.LIST,
    subject: Subjects.ORG_DASHBOARD,
  },
  {
    id: 'navigation.nav-projects',
    domain: 'navigation',
    labelKey: 'capabilities.navigation.navProjects',
    action: Actions.READ,
    subject: Subjects.PROJECT,
  },
  {
    id: 'navigation.nav-orders',
    domain: 'navigation',
    labelKey: 'capabilities.navigation.navOrders',
    action: Actions.LIST,
    subject: Subjects.ORDER,
  },
  {
    id: 'navigation.nav-claims',
    domain: 'navigation',
    labelKey: 'capabilities.navigation.navClaims',
    action: Actions.LIST,
    subject: Subjects.ORDER,
  },
  {
    id: 'navigation.nav-accounts',
    domain: 'navigation',
    labelKey: 'capabilities.navigation.navAccounts',
    action: Actions.LIST,
    subject: Subjects.ORDER,
  },
  {
    id: 'navigation.nav-vendors',
    domain: 'navigation',
    labelKey: 'capabilities.navigation.navVendors',
    action: Actions.READ,
    subject: Subjects.ORG_VENDOR,
  },
  {
    id: 'navigation.nav-client-variations',
    domain: 'navigation',
    labelKey: 'capabilities.navigation.navClientVariations',
    action: Actions.READ,
    subject: Subjects.CLIENT_DASHBOARD,
  },
];
